import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { Link } from 'react-router-dom'
import { ROUTES } from '@shared/config/routes'
import { emailFormSchema, emailOnlySchema, type EmailFormValues, type EmailOnlyValues } from '../../lib/emailSchema'
import styles from './EmailStep.module.css'

interface EmailStepProps {
  /** Стартовое значение поля email */
  initialEmail?: string
  /** Заголовок формы */
  title?: string
  /** Текст кнопки отправки */
  submitLabel?: string
  /** Обработчик отправки валидного email (уже нормализованного) */
  onSubmit: (email: string) => Promise<void>
  /** Индикатор процесса отправки */
  isSubmitting?: boolean
  /** Общая ошибка под формой (от сервера) */
  errorText?: string | null
  /** Показывать ли чекбокс согласия (для авторизации — да, для привязки — нет) */
  showAgreement?: boolean
}

/**
 * Шаг ввода email: поле с валидацией и опциональным чекбоксом согласия.
 * @param initialEmail - Стартовое значение поля
 * @param title - Заголовок формы
 * @param submitLabel - Текст кнопки отправки
 * @param onSubmit - Колбэк с валидным нормализованным email
 * @param isSubmitting - Идёт ли отправка
 * @param errorText - Серверная ошибка
 * @param showAgreement - Показывать ли чекбокс согласия
 */
export const EmailStep = ({
  initialEmail = '',
  title = 'Войти через почту',
  submitLabel = 'Получить код',
  onSubmit,
  isSubmitting = false,
  errorText = null,
  showAgreement = true
}: EmailStepProps) => {
  type Values = EmailFormValues | EmailOnlyValues

  const {
    register,
    handleSubmit,
    formState: { errors, isValid }
  } = useForm<Values>({
    resolver: yupResolver(showAgreement ? emailFormSchema : emailOnlySchema) as never,
    mode: 'onChange',
    defaultValues: showAgreement
      ? { email: initialEmail, agreed: false }
      : { email: initialEmail }
  })

  const submit = async (values: Values) => {
    await onSubmit(values.email)
  }

  const emailError = errors.email?.message
  const agreedError = 'agreed' in errors ? errors.agreed?.message : undefined
  const inputClass = [styles.input, emailError && styles.inputInvalid].filter(Boolean).join(' ')

  return (
    <form onSubmit={handleSubmit(submit)} className={styles.form} noValidate>
      <div className={styles.header}>
        <h1 className={styles.title}>{title}</h1>

        <div className={styles.fields}>
          <input
            id="auth-email"
            type="email"
            autoComplete="email"
            autoFocus
            placeholder="Email"
            className={inputClass}
            {...register('email')}
          />

          {showAgreement && (
            <label className={styles.agreement}>
              <input type="checkbox" className={styles.checkbox} {...register('agreed' as never)} />
              <span className={styles.agreementText}>
                Соглашаюсь с{' '}
                <Link to={ROUTES.PRIVACY} target="_blank" rel="noreferrer" className={styles.link}>
                  политикой сбора и обработки персональных данных
                </Link>
                {' и '}
                <Link to={ROUTES.TERMS} target="_blank" rel="noreferrer" className={styles.link}>
                  условиями пользования приложением
                </Link>
              </span>
            </label>
          )}

          {agreedError && <span className={styles.error}>{agreedError}</span>}
          {errorText && <span className={styles.error}>{errorText}</span>}
        </div>
      </div>

      <button type="submit" disabled={!isValid || isSubmitting} className={styles.submit}>
        {isSubmitting ? 'Отправляем…' : submitLabel}
      </button>
    </form>
  )
}
